"use client"

import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, useState } from "react"
import {
  type UseIntersectionObserverOptions,
  useIntersectionObserver,
} from "@/hooks/browser/observers/use-intersection-observer.hook"
import { useTracking } from "@/hooks/tracking/use-tracking"
import type { InView } from "../emitter/tracking-emitter.types"

type UseInViewTrackingProps = UseIntersectionObserverOptions & {
  readonly model: InView["model"]
  readonly intersectionObserverOptions?: UseIntersectionObserverOptions
}

export function useInViewTracking<ReferenceType extends HTMLElement>({
  intersectionObserverOptions,
  model,
}: UseInViewTrackingProps) {
  const [isTracked, setIsTracked] = useState(false)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { emit } = useTracking()

  const { isIntersecting: isInView, elementRef: ref } =
    useIntersectionObserver<ReferenceType>(intersectionObserverOptions)

  useEffect(() => {
    if (isInView && !isTracked) {
      emit({
        event: "in-view",
        model,
      })
      setIsTracked(true)
    }
  }, [emit, isInView, isTracked, model])

  // biome-ignore lint/correctness/useExhaustiveDependencies: We ignore biome here we want to reset tracking when the path or search params change
  useEffect(() => {
    // On any new page we track if the element comes in view again
    setIsTracked(false)
  }, [pathname, searchParams])

  return ref
}
